import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { sha256 } from 'js-sha256'
import DeleteIcon from '@mui/icons-material/Delete'
import TuneIcon from '@mui/icons-material/Tune'
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PopoverPanel } from '@ui2/structure'
import { AddButton, SaveButton, SwitchLabeled } from '@ui/actions'
import { Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useResultsInvalidation, useSceneShapeMarker } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import {
  getFilterViews,
  getModel,
  getSeismicSettings,
  getSimplifiedSeismicModelConfig,
  getSimplifiedSeismicModelInputs,
  getSimplifiedSeismicResults,
} from '@queries'
import { storeyToLabel } from 'src/components/pages/Editor/pages/Plans/components/AddPlanDialog/components/utils'
import { buildErrorMessage } from 'src/constants/errors'
import {
  addStoreyAtTop,
  removeStoreyAtTop,
  updateStoreyBoundaries,
} from 'src/state/mutations/model'

type StoreyHeightRow = {
  storey: number
  height: number
}

type StoreyBoundariesFormValues = {
  rows: {
    storey: number
    heightInput: string
  }[]
  settings: typeof defaultStoreyBoundariesUpdateSettings
}

const defaultStoreyBoundariesUpdateSettings = {
  translateSlabs: true,
  translateVerticalSlabs: true,
  translateRoofSlabs: true,
  translateVerticalRoofSlabs: true,
  translateBeams: true,
  translatePurlins: true,
  updateColumnHeights: true,
}

type ScenePoint = {
  x: number
  y: number
  z: number
}

const decimalFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
})

const formatThreeDecimals = (value: number): string => decimalFormatter.format(value)

const parseGermanDecimal = (value: string): number => {
  const normalized = value.trim().replace(/\./g, '').replace(',', '.')
  return Number(normalized)
}

const STOREY_MARKER_PADDING = 0.5

// Stable identity: Form's reinitialize effect depends on this array, so an inline literal would
// reset the height inputs to the (stale) store values on every re-render, e.g. while saving.
const preserveOnReinitialize = ['settings']

const DEFAULT_MARKER_SHAPE_POINTS: Point[] = [
  new ImmutableVector3(0, 0, 0),
  new ImmutableVector3(10, 0, 0),
  new ImmutableVector3(10, 10, 0),
  new ImmutableVector3(0, 10, 0),
]

const getStoreyMarkerPoints = (points: Point[], z: number): ScenePoint[] => {
  if (!Number.isFinite(z)) return []

  const shapePoints = points.length > 0 ? points : DEFAULT_MARKER_SHAPE_POINTS

  const bounds = shapePoints.reduce(
    (acc, point) => ({
      minX: Math.min(acc.minX, point.x),
      maxX: Math.max(acc.maxX, point.x),
      minY: Math.min(acc.minY, point.y),
      maxY: Math.max(acc.maxY, point.y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  )

  return [
    { x: bounds.minX - STOREY_MARKER_PADDING, y: bounds.minY - STOREY_MARKER_PADDING, z },
    { x: bounds.maxX + STOREY_MARKER_PADDING, y: bounds.minY - STOREY_MARKER_PADDING, z },
    { x: bounds.maxX + STOREY_MARKER_PADDING, y: bounds.maxY + STOREY_MARKER_PADDING, z },
    { x: bounds.minX - STOREY_MARKER_PADDING, y: bounds.maxY + STOREY_MARKER_PADDING, z },
  ]
}

const StoreyZValueMarker = ({ z, points }: { z: number; points: Point[] }): null => {
  const markerPoints = useMemo(() => getStoreyMarkerPoints(points, z), [points, z])

  useSceneShapeMarker({
    points: markerPoints,
    enabled: markerPoints.length > 0,
    color: 'blue',
    lineWidth: 3,
    renderOrder: 999,
  })
  return null
}

const StoreyBoundariesForm = (): ReactElement => {
  const model = useModelStore(state => state.model)
  const includeStoreyInVisibility = useModelStore(state => state.includeStoreyInVisibility)
  const excludeStoreyFromVisibility = useModelStore(state => state.excludeStoreyFromVisibility)
  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const invalidateResults = useResultsInvalidation()
  const invalidateModel = useModelInvalidation()
  const queryClient = useQueryClient()
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null)

  const initialRows = useMemo<StoreyHeightRow[]>(
    () =>
      Object.entries(model.storey_boundaries)
        .map(([storey, [lower, upper]]) => ({
          storey: Number(storey),
          height: upper - lower,
        }))
        .sort((a, b) => b.storey - a.storey),
    [model.storey_boundaries],
  )

  const invalidateSeismicSettings = () => {
    const id = projectId as string
    queryClient.invalidateQueries(getSeismicSettings.getKey(id))
    queryClient.invalidateQueries(getSimplifiedSeismicModelConfig.getKey(id))
    queryClient.invalidateQueries(getSimplifiedSeismicModelInputs.getKey(id))
    queryClient.invalidateQueries(getSimplifiedSeismicResults.getKey(id))
  }

  const { mutate: submit, isLoading: isSaving } = useMutation(
    async (values: StoreyBoundariesFormValues) => {
      const parsedRows = values.rows.map(row => ({
        storey: row.storey,
        height: parseGermanDecimal(row.heightInput),
      }))
      if (parsedRows.some(row => !Number.isFinite(row.height) || row.height <= 0)) {
        throw new Error('Bitte geben Sie nur positive numerische Stockwerkshöhen ein.')
      }

      const ascending = [...parsedRows].sort((a, b) => a.storey - b.storey)
      const storeyBoundaries: StoreyBoundaries = {}
      let lower = 0
      for (const row of ascending) {
        const upper = lower + row.height
        storeyBoundaries[row.storey.toString()] = [lower, upper]
        lower = upper
      }

      return updateStoreyBoundaries.request(projectId as string, storeyBoundaries, values.settings)
    },
    {
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        await invalidateResults(projectId as string)
        invalidateSeismicSettings()
        enqueueSnackbar(
          'Stockwerkshöhen erfolgreich aktualisiert. Erdbebenlasten wurden zurückgesetzt',
          { variant: 'success' },
        )
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Aktualisieren der Stockwerkshöhen'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: createTopStorey, isLoading: isAddingStorey } = useMutation(
    () => addStoreyAtTop.request(projectId as string),
    {
      onSuccess: async () => {
        const nextTopStorey = (
          Math.max(...Object.keys(model.storey_boundaries).map(Number)) + 1
        ).toString()
        includeStoreyInVisibility(nextTopStorey)
        await queryClient.invalidateQueries({
          queryKey: getFilterViews.getKey(projectId as string),
        })
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        invalidateSeismicSettings()
        enqueueSnackbar('Stockwerk erfolgreich hinzugefügt. Erdbebenlasten wurden zurückgesetzt', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hinzufügen eines Stockwerks'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: deleteTopStorey, isLoading: isRemovingStorey } = useMutation(
    () => removeStoreyAtTop.request(projectId as string),
    {
      onSuccess: () => {
        const removedStorey = Math.max(
          ...Object.keys(model.storey_boundaries).map(Number),
        ).toString()
        excludeStoreyFromVisibility(removedStorey)
        queryClient.invalidateQueries({ queryKey: getFilterViews.getKey(projectId as string) })
        queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        invalidateSeismicSettings()
        enqueueSnackbar(
          'Oberstes Stockwerk erfolgreich entfernt. Erdbebenlasten wurden zurückgesetzt',
          { variant: 'success' },
        )
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Entfernen des obersten Stockwerks'), {
          variant: 'error',
        })
      },
    },
  )

  const defaultValues = useMemo<StoreyBoundariesFormValues>(
    () => ({
      rows: initialRows.map(row => ({
        storey: row.storey,
        heightInput: formatThreeDecimals(row.height),
      })),
      settings: defaultStoreyBoundariesUpdateSettings,
    }),
    [initialRows],
  )

  const topNumericStorey = useMemo(() => {
    return Math.max(...Object.keys(model.storey_boundaries).map(Number))
  }, [model.storey_boundaries])

  const storeyShapePoints = useMemo(() => {
    const shapePointsByStorey: Record<number, Point[]> = {}

    model.slabs.forEach(slab => {
      const storey = Number(slab.storey)
      if (!Number.isFinite(storey)) return

      shapePointsByStorey[storey] = [...(shapePointsByStorey[storey] ?? []), ...slab.shape.points]
    })

    if (Number.isFinite(topNumericStorey)) {
      model.roof_slabs.forEach(roofSlab => {
        shapePointsByStorey[topNumericStorey] = [
          ...(shapePointsByStorey[topNumericStorey] ?? []),
          ...roofSlab.shape.points,
        ]
      })
    }

    return shapePointsByStorey
  }, [model.roof_slabs, model.slabs, topNumericStorey])

  const storeyRowsRenderVersion = useMemo(
    () => sha256(JSON.stringify(model.storey_boundaries)),
    [model.storey_boundaries],
  )

  return (
    <Box>
      <Form
        key={storeyRowsRenderVersion}
        defaultValues={defaultValues}
        enableReinitialize
        preserveOnReinitialize={preserveOnReinitialize}
        onSubmit={values => submit(values as StoreyBoundariesFormValues)}
      >
        {methods => {
          const rows = (methods.watch('rows') ?? []) as StoreyBoundariesFormValues['rows']
          const settings = methods.watch('settings') as StoreyBoundariesFormValues['settings']
          const topStorey = rows.reduce(
            (maxStorey, row) => Math.max(maxStorey, row.storey),
            Number.NEGATIVE_INFINITY,
          )

          const zValueByStorey = (() => {
            const parsedRows = rows.map(row => ({
              storey: row.storey,
              height: Math.max(0, parseGermanDecimal(row.heightInput) || 0),
            }))
            const ascending = [...parsedRows].sort((a, b) => a.storey - b.storey)
            const zLookup: Record<number, number> = {}
            let cumulative = 0
            for (const row of ascending) {
              cumulative += row.height
              zLookup[row.storey] = cumulative
            }
            return zLookup
          })()

          const roofTopZ = (() => {
            const parsedRows = rows.map(row => ({
              height: Math.max(0, parseGermanDecimal(row.heightInput) || 0),
            }))
            return parsedRows.reduce((sum, row) => sum + row.height, 0)
          })()

          const bottomStorey = rows.reduce(
            (minStorey, row) => Math.min(minStorey, row.storey),
            Number.POSITIVE_INFINITY,
          )

          return (
            <Stack spacing={1}>
              {rows.map((row, rowIndex) => (
                <StoreyZValueMarker
                  key={`storey-z-marker-${row.storey}-${rowIndex}-${storeyRowsRenderVersion}`}
                  z={zValueByStorey[row.storey] ?? 0}
                  points={storeyShapePoints[row.storey] ?? []}
                />
              ))}
              <StoreyZValueMarker
                key={`storey-z-marker-base-${storeyRowsRenderVersion}`}
                z={0}
                points={storeyShapePoints[bottomStorey] ?? []}
              />
              <StoreyZValueMarker
                key={`storey-z-marker-roof-top-${storeyRowsRenderVersion}`}
                z={roofTopZ}
                points={storeyShapePoints[topStorey] ?? []}
              />
              <Stack spacing={1}>
                <AddButton
                  loading={isAddingStorey}
                  onClick={() => createTopStorey()}
                  fullWidth
                  disabled={isRemovingStorey || methods.formState.isDirty}
                  data-cy="btn-add-top-storey"
                  size="small"
                >
                  SW hinzufügen
                </AddButton>
                <TableContainer sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 72 }}>SW</TableCell>
                        <TableCell sx={{ width: 160 }}>Höhe</TableCell>
                        <TableCell sx={{ width: 120 }}>Höhe cum.</TableCell>
                        <TableCell align="center" sx={{ width: 90 }}>
                          Aktionen
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow key={`${row.storey}-${index}-${storeyRowsRenderVersion}`}>
                          <TableCell>
                            <Typography noWrap>{storeyToLabel(row.storey.toString())}</Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="text"
                              value={row.heightInput}
                              slotProps={{
                                htmlInput: {
                                  inputMode: 'decimal',
                                },
                                input: {
                                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                                },
                              }}
                              onChange={event =>
                                methods.setValue(`rows.${index}.heightInput`, event.target.value, {
                                  shouldDirty: true,
                                })
                              }
                              onBlur={() => {
                                const parsed = parseGermanDecimal(
                                  methods.getValues(`rows.${index}.heightInput`),
                                )
                                if (Number.isFinite(parsed) && parsed > 0) {
                                  methods.setValue(
                                    `rows.${index}.heightInput`,
                                    formatThreeDecimals(parsed),
                                    { shouldDirty: true },
                                  )
                                }
                              }}
                              sx={{ width: 120 }}
                            />
                          </TableCell>
                          <TableCell>
                            {formatThreeDecimals(zValueByStorey[row.storey] ?? 0)}m
                          </TableCell>
                          <TableCell align="center">
                            {row.storey === topStorey ? (
                              <IconButton
                                size="small"
                                onClick={() => deleteTopStorey()}
                                disabled={isRemovingStorey || isAddingStorey || rows.length <= 1}
                                data-cy="btn-remove-top-storey"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>

              <PopoverPanel
                anchorEl={settingsAnchorEl}
                onClose={() => setSettingsAnchorEl(null)}
                title="Einstellungen"
                dataCy="storey-boundaries-settings-popover"
              >
                <Stack spacing={0.5}>
                  <SwitchLabeled
                    checked={settings?.translateSlabs ?? true}
                    onChange={event =>
                      methods.setValue('settings.translateSlabs', event.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    label="Deckenflächen verschieben"
                  />
                  <SwitchLabeled
                    checked={settings?.translateVerticalSlabs ?? false}
                    onChange={event =>
                      methods.setValue('settings.translateVerticalSlabs', event.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    label="Deckenpositionen verschieben"
                  />
                  <SwitchLabeled
                    checked={settings?.translateRoofSlabs ?? true}
                    onChange={event =>
                      methods.setValue('settings.translateRoofSlabs', event.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    label="Dachflächen verschieben"
                  />
                  <SwitchLabeled
                    checked={settings?.translateVerticalRoofSlabs ?? false}
                    onChange={event =>
                      methods.setValue(
                        'settings.translateVerticalRoofSlabs',
                        event.target.checked,
                        {
                          shouldDirty: true,
                        },
                      )
                    }
                    label="Dachpositionen verschieben"
                  />
                  <SwitchLabeled
                    checked={settings?.translateBeams ?? false}
                    onChange={event =>
                      methods.setValue('settings.translateBeams', event.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    label="Unterzüge verschieben"
                  />
                  <SwitchLabeled
                    checked={settings?.translatePurlins ?? false}
                    onChange={event =>
                      methods.setValue('settings.translatePurlins', event.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    label="Pfetten verschieben"
                  />
                  <SwitchLabeled
                    checked={settings?.updateColumnHeights ?? false}
                    onChange={event =>
                      methods.setValue('settings.updateColumnHeights', event.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    label="Länge der Stützen aktualisieren"
                  />
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Diese Einstellungen steuern das Verhalten beim Aktualisieren der
                    Stockwerkshöhen.
                  </Alert>
                </Stack>
              </PopoverPanel>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!methods.formState.isDirty || isAddingStorey || isRemovingStorey}
                  onClick={() => methods.reset()}
                  data-cy="btn-reset-storey-boundaries"
                  sx={{ flex: 1 }}
                >
                  Zurücksetzen
                </Button>
                <SaveButton
                  variant="contained"
                  type="submit"
                  loading={isSaving}
                  disabled={!methods.formState.isDirty || isAddingStorey || isRemovingStorey}
                  sx={{ flex: 1 }}
                >
                  speichern
                </SaveButton>
                <IconButton
                  size="small"
                  onClick={event => setSettingsAnchorEl(event.currentTarget)}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                  data-cy="btn-storey-boundaries-settings"
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          )
        }}
      </Form>
    </Box>
  )
}

export default StoreyBoundariesForm
